/**
 * sessionStore.ts
 * Auto-saves the wizard configuration so a refresh or an accidental tab close
 * does not throw away the mapping work.
 *
 * Only configuration is stored — never the Excel rows themselves, which stay in
 * memory and must be reloaded from the file. The key is derived from the pair
 * of files in play so two different jobs never restore into each other.
 */

import type { FieldFormattingConfig } from "@/utils/formatter";
import type { TemplateLayoutEdit } from "@/utils/templateLayoutEdits";
import type { TemplateOverlay } from "@/utils/templateOverlays";

const STORAGE_PREFIX = "mini_printer_session_v1:";

/** Sessions older than this are dropped on read. */
export const SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export interface SavedSession {
  savedAt: number;
  step: "data" | "group" | "template" | "preview" | "upload" | "review" | "mapping" | "bind";
  selectedSheet: string;
  /** Template field name → Excel column. */
  mappings: Record<string, string>;
  formats: Record<string, FieldFormattingConfig>;
  requiredOverrides: Record<string, boolean>;
  groupingColumns: string[];
  /** @deprecated kept for backward compatibility with older sessions. */
  selectedGroups?: string[];
  /** Export list: group values in order, with their display names. */
  addedPages: { groupValue: string; label: string; enabled?: boolean }[];
  lookupSheet: string;
  joinKeyPrimary: string;
  joinKeySecondary: string;
  layoutEdits: TemplateLayoutEdit[];
  /** Free-form text/image overlays added on top of template pages. */
  overlays?: TemplateOverlay[];
    /**
     * When true, missing Excel values / unmapped fields print blank (no
     * {{placeholder}} ghost text). Default true for production printouts.
     */
    printEmptyWhenMissing?: boolean;
  }

/**
 * FNV-1a over the file bytes. Large files are sampled rather than hashed whole
 * so switching files stays instant; combined with the byte length this is
 * plenty to tell two working files apart.
 */
export function hashBytes(buffer: ArrayBuffer | Uint8Array, sampleSize = 65536): string {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  const stride = Math.max(1, Math.floor(bytes.length / sampleSize));
  let hash = 0x811c9dc5;
  for (let i = 0; i < bytes.length; i += stride) {
    hash ^= bytes[i];
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `${bytes.length.toString(36)}-${hash.toString(36)}`;
}

export function sessionKey(excelHash: string, templateHash: string): string {
  return `${STORAGE_PREFIX}${excelHash}_${templateHash}`;
}

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function defaultStorage(): StorageLike | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

export function saveSession(
  key: string,
  session: Omit<SavedSession, "savedAt">,
  storage: StorageLike | null = defaultStorage()
): void {
  if (!storage) return;
  try {
    const payload: SavedSession = { ...session, savedAt: Date.now() };
    storage.setItem(key, JSON.stringify(payload));
  } catch {
    // Quota or private-mode failures must never break the flow.
  }
}

/** Returns the stored session, or null when absent, unreadable or expired. */
export function loadSession(
  key: string,
  storage: StorageLike | null = defaultStorage(),
  now = Date.now()
): SavedSession | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SavedSession;
    if (!parsed || typeof parsed.savedAt !== "number") return null;
    if (now - parsed.savedAt > SESSION_TTL_MS) {
      storage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearSession(key: string, storage: StorageLike | null = defaultStorage()): void {
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    // ignore
  }
}
